import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Building2, LogOut, Plus, Users, Award, UserPlus, 
  Mail, Clock, CheckCircle, XCircle, Eye, Settings,
  Archive, ArchiveRestore, Trophy, FileText, AlertTriangle, Download, Palette, Edit, Trash2, Save, Calculator,
  TrendingUp, GraduationCap, BarChart3, Medal, Search, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import TemplateManager from '@/components/TemplateManager';
import FormBuilder from '@/components/FormBuilder';
import ScoringTemplateEditor from '@/components/ScoringTemplateEditor';
import ApplicationScoring from '@/components/ApplicationScoring';

import CommitteeScholarshipsTab from './committee/CommitteeScholarshipsTab';
import CommitteeAccessRequestsTab from './committee/CommitteeAccessRequestsTab';
import CommitteeMembersTab from './committee/CommitteeMembersTab';
import CommitteeApplicantsTab from './committee/CommitteeApplicantsTab';
import CommitteeMeritReportTab from './committee/CommitteeMeritReportTab';
import CommitteeScholarshipModal from './committee/CommitteeScholarshipModal';
import ApplicantProfileModal from './committee/ApplicantProfileModal';
import CommitteeLibraryTab from './committee/CommitteeLibraryTab';
import CommitteeLandingPageTab from './committee/CommitteeLandingPageTab';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommitteeDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [committees, setCommittees] = useState([]);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [scholarships, setScholarships] = useState([]);
  const [allScholarships, setAllScholarships] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scholarships');
  const [showArchived, setShowArchived] = useState(false);
  
  // Dialog states
  const [inviteDialog, setInviteDialog] = useState(false);
  const [memberLimits, setMemberLimits] = useState(null);
  const [archiveDialog, setArchiveDialog] = useState({ open: false, application: null });
  const [awardDialog, setAwardDialog] = useState({ open: false, application: null });
  const [profileDialog, setProfileDialog] = useState({ open: false, applicant: null });
  const [scoringDialog, setScoringDialog] = useState({ open: false, application: null, scholarship: null });
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'member'
  });
  const [archiveForm, setArchiveForm] = useState({ reason: '', archive_user: false });
  const [awardNotes, setAwardNotes] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Merit Report state
  const [meritReport, setMeritReport] = useState(null);
  const [meritLoading, setMeritLoading] = useState(false);
  const [selectedGpaScale, setSelectedGpaScale] = useState(4);

  // Landing page editor state
  const [landingPageForm, setLandingPageForm] = useState({
    slug: '',
    hero_title: '',
    hero_subtitle: '',
    about_title: '',
    about_description: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    scholarships_title: '',
    scholarships_subtitle: '',
    logo_url: '',
    banner_url: ''
  });
  const [savingLandingPage, setSavingLandingPage] = useState(false);
  
  // Default form configuration for scholarships
  const DEFAULT_FORM_CONFIG = [
    {
      id: 'personal',
      title: 'Personal Information',
      enabled: true,
      fields: [
        { id: 'full_name', label: 'Full Legal Name', type: 'text', required: true },
        { id: 'email', label: 'Email Address', type: 'email', required: true },
        { id: 'phone', label: 'Phone Number', type: 'tel', required: true },
        { id: 'address', label: 'Mailing Address', type: 'textarea', required: true },
        { id: 'date_of_birth', label: 'Date of Birth', type: 'date', required: true }
      ]
    },
    {
      id: 'education',
      title: 'Educational Background',
      enabled: true,
      fields: [
        { id: 'current_school', label: 'Current/Most Recent School', type: 'text', required: true },
        { id: 'gpa', label: 'GPA', type: 'number', required: true },
        { id: 'major', label: 'Major/Field of Study', type: 'text', required: true },
        { id: 'graduation_date', label: 'Expected Graduation Date', type: 'date', required: true }
      ]
    },
    {
      id: 'greek_heritage',
      title: 'Greek Heritage & AHEPA Connection',
      enabled: true,
      fields: [
        { id: 'greek_heritage_description', label: 'Describe your Greek heritage', type: 'textarea', required: true },
        { id: 'ahepa_connection', label: 'AHEPA/DOP Family Connection', type: 'textarea', required: false }
      ]
    },
    {
      id: 'activities',
      title: 'Activities & Leadership',
      enabled: true,
      fields: [
        { id: 'extracurricular', label: 'Extracurricular Activities', type: 'textarea', required: false },
        { id: 'community_service', label: 'Community Service', type: 'textarea', required: false },
        { id: 'leadership_roles', label: 'Leadership Positions', type: 'textarea', required: false }
      ]
    },
    {
      id: 'essays',
      title: 'Essays',
      enabled: true,
      fields: [
        { id: 'personal_statement', label: 'Personal Statement', type: 'richtext', required: true, maxLength: 2500 },
        { id: 'goals_essay', label: 'Future Goals', type: 'richtext', required: true, maxLength: 1500 }
      ]
    },
    {
      id: 'documents',
      title: 'Supporting Documents',
      enabled: true,
      fields: [
        { id: 'transcript', label: 'Official Transcript', type: 'file', required: false, accept_file_types: '.pdf,.docx,.xls,.csv,.jpg,.jpeg,.png' },
        { id: 'enrollment_proof', label: 'Proof of Enrollment', type: 'file', required: false, accept_file_types: '.pdf,.docx,.xls,.csv,.jpg,.jpeg,.png' }
      ]
    },
    {
      id: 'endorsement',
      title: 'Chapter Endorsement',
      enabled: true,
      fields: [
        { id: 'endorser_name', label: 'Endorser Name', type: 'text', required: true },
        { id: 'endorser_email', label: 'Endorser Email', type: 'email', required: true },
        { id: 'endorser_phone', label: 'Endorser Phone', type: 'tel', required: false },
        { id: 'chapter_name', label: 'Chapter Name/Number', type: 'text', required: true }
      ]
    },
    {
      id: 'review',
      title: 'Review & Submit',
      enabled: true,
      fields: []
    }
  ];

  // Scholarship edit/create dialog
  const [scholarshipDialog, setScholarshipDialog] = useState({ open: false, editing: null });
  const [editTab, setEditTab] = useState('details');
  const [scholarshipTypes, setScholarshipTypes] = useState([
    { id: 'first_year', name: 'First Year' },
    { id: 'continuing', name: 'Continuing Education' },
    { id: 'graduate', name: 'Graduate Studies' }
  ]);
  const [scholarshipForm, setScholarshipForm] = useState({
    name: '',
    description: '',
    amount: 1000,
    start_date: '',
    deadline: '',
    eligibility: '',
    scholarship_type: 'first_year',
    scoring_criteria: [
      { name: 'Academic Merit', weight: 0.3, max_score: 10, description: 'Academic achievements and GPA' },
      { name: 'Community Involvement', weight: 0.25, max_score: 10, description: 'Volunteer and community activities' },
      { name: 'Greek Heritage', weight: 0.2, max_score: 10, description: 'Connection to Greek heritage and AHEPA' },
      { name: 'Leadership', weight: 0.15, max_score: 10, description: 'Leadership roles and potential' },
      { name: 'Aspirations', weight: 0.1, max_score: 10, description: 'Future goals and career plans' }
    ],
    form_config: DEFAULT_FORM_CONFIG,
    is_active: true,
    is_archived: false,
    allow_previous_recipients: true,
    scoring_template: { sections: [] }
  });
  const [savingScholarship, setSavingScholarship] = useState(false);

  // Download application as PDF
  const downloadApplicationPdf = async (applicationId, applicantName) => {
    setDownloadingPdf(applicationId);
    try {
      const response = await axios.get(`${API}/applications/${applicationId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${applicantName.replace(/[^a-zA-Z0-9]/g, '_')}_Application.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      if (error.response?.status === 503) {
        toast.error('PDF generation is not configured. Please contact administrator.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to download PDF');
      }
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Open scholarship dialog for create or edit
  const createTempId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

  const formatDeadlineForInput = (deadline) => {
    if (!deadline) return '';
    if (deadline.length === 10) return `${deadline}T23:59`;
    if (deadline.length >= 16) return deadline.slice(0, 16);
    return deadline;
  };

  const normalizeFormConfigLocal = (formConfig = []) => {
    if (!Array.isArray(formConfig)) return [];
    return formConfig.map((section) => {
      const fields = Array.isArray(section.fields) ? section.fields : [];
      return {
        ...section,
        id: section.id || createTempId('section'),
        title: section.title || 'Untitled Section',
        description: section.description || '',
        enabled: section.enabled !== false,
        fields: fields.map((field) => ({
          ...field,
          id: field.id || createTempId('field'),
          label: field.label || 'Untitled Field',
          type: field.type || 'text',
          required: !!field.required,
          options: Array.isArray(field.options) ? field.options : [],
          sub_fields: Array.isArray(field.sub_fields) ? field.sub_fields : []
        }))
      };
    });
  };

  const getDefaultEligibility = (level) => {
    switch (level?.toLowerCase()) {
      case 'national':
        return 'Open to all applicants nationally. Endorsers must be members of the AHEPA or DOP orders.';
      case 'district':
        return 'Open to all applicants within the district. Endorsers must be members of the AHEPA or DOP orders.';
      case 'chapter':
      default:
        return 'Open to anyone in the chapter or near the chapter if there is no chapter in their area. Endorsers must be members of the AHEPA or DOP orders.';
    }
  };

  const openScholarshipDialog = (scholarship = null) => {
    setEditTab('details');
    if (scholarship) {
      setScholarshipForm({
        name: scholarship.name || '',
        description: scholarship.description || '',
        amount: scholarship.amount || 1000,
        start_date: scholarship.start_date || '',
        deadline: formatDeadlineForInput(scholarship.deadline),
        eligibility: scholarship.eligibility || '',
        scholarship_type: scholarship.scholarship_type || 'first_year',
        scoring_criteria: scholarship.scoring_criteria || [
          { name: 'Academic Merit', weight: 0.3, max_score: 10, description: 'Academic achievements and GPA' },
          { name: 'Community Involvement', weight: 0.25, max_score: 10, description: 'Volunteer and community activities' },
          { name: 'Greek Heritage', weight: 0.2, max_score: 10, description: 'Connection to Greek heritage and AHEPA' },
          { name: 'Leadership', weight: 0.15, max_score: 10, description: 'Leadership roles and potential' },
          { name: 'Aspirations', weight: 0.1, max_score: 10, description: 'Future goals and career plans' }
        ],
        form_config: normalizeFormConfigLocal(scholarship.form_config || DEFAULT_FORM_CONFIG),
        is_active: scholarship.is_active !== false,
        is_archived: scholarship.is_archived || false,
        allow_previous_recipients: scholarship.allow_previous_recipients !== false,
        scoring_template: scholarship.scoring_template || { sections: [] }
      });
      setScholarshipDialog({ open: true, editing: scholarship.id });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setScholarshipForm({
        name: '',
        description: '',
        amount: 1000,
        start_date: today,
        deadline: '',
        eligibility: getDefaultEligibility(selectedCommittee?.level),
        scholarship_type: 'first_year',
        scoring_criteria: [
          { name: 'Academic Merit', weight: 0.3, max_score: 10, description: 'Academic achievements and GPA' },
          { name: 'Community Involvement', weight: 0.25, max_score: 10, description: 'Volunteer and community activities' },
          { name: 'Greek Heritage', weight: 0.2, max_score: 10, description: 'Connection to Greek heritage and AHEPA' },
          { name: 'Leadership', weight: 0.15, max_score: 10, description: 'Leadership roles and potential' },
          { name: 'Aspirations', weight: 0.1, max_score: 10, description: 'Future goals and career plans' }
        ],
        form_config: DEFAULT_FORM_CONFIG,
        is_active: true,
        is_archived: false,
        allow_previous_recipients: true,
        scoring_template: { sections: [] }
      });
      setScholarshipDialog({ open: true, editing: null });
    }
  };

  // Save scholarship (create or update)

  const cloneFromLibrary = (scholarship) => {
    // Scrub the sponsor and review sections of any custom descriptions
    const scrubbedConfig = (scholarship.form_config || []).map(section => {
      if (section.id === 'sponsor') {
        return { ...section, title: "Endorsement", description: "Provide your endorser's information. They will receive an email to complete the endorsement." };
      }
      if (section.id === 'review') {
        return { ...section, title: "Review & Submit", description: "Review your application before submission" };
      }
      return section;
    });

    const today = new Date().toISOString().split('T')[0];
    setEditTab('details');
    setScholarshipForm({
      name: `${scholarship.name} (Cloned)`,
      description: scholarship.description,
      amount: scholarship.amount,
      start_date: today,
      deadline: formatDeadlineForInput(scholarship.deadline),
      eligibility: scholarship.eligibility,
      scholarship_type: scholarship.scholarship_type,
      scoring_criteria: (scholarship.scoring_criteria || []).map(c => ({ ...c })),
      form_config: normalizeFormConfigLocal(scrubbedConfig),
      is_active: false, // Start as inactive draft
      is_archived: false,
      allow_previous_recipients: scholarship.allow_previous_recipients !== false,
      scoring_template: scholarship.scoring_template ? JSON.parse(JSON.stringify(scholarship.scoring_template)) : { sections: [] }
    });
    setScholarshipDialog({ open: true, editing: null }); // null means create new
    toast.info('Scholarship cloned from library. Please edit details and save to create.');
  };

  const saveScholarship = async (closeAfterSave = true) => {
    if (!scholarshipForm.name || !scholarshipForm.deadline) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSavingScholarship(true);
    try {
      const dataToSave = {
        ...scholarshipForm,
        committee_id: selectedCommittee.id
      };
      
      if (scholarshipDialog.editing) {
        await axios.put(`${API}/scholarships/${scholarshipDialog.editing}`, dataToSave);
        toast.success('Scholarship updated successfully');
      } else {
        await axios.post(`${API}/scholarships`, dataToSave);
        toast.success('Scholarship created successfully');
      }
      
      if (closeAfterSave) {
        setScholarshipDialog({ open: false, editing: null });
      }
      fetchCommitteeData();
    } catch (error) {
      console.error('Error saving scholarship:', error);
      toast.error(error.response?.data?.detail || 'Failed to save scholarship');
    } finally {
      setSavingScholarship(false);
    }
  };

  const saveLandingPage = async () => {
    if (!selectedCommittee?.id) return;
    if (!landingPageForm.slug) {
      toast.error('Landing page slug is required');
      return;
    }

    setSavingLandingPage(true);
    try {
      const payload = {
        slug: landingPageForm.slug,
        landing_page: {
          hero_title: landingPageForm.hero_title,
          hero_subtitle: landingPageForm.hero_subtitle,
          about_title: landingPageForm.about_title,
          about_description: landingPageForm.about_description,
          contact_email: landingPageForm.contact_email,
          contact_phone: landingPageForm.contact_phone,
          contact_address: landingPageForm.contact_address,
          scholarships_title: landingPageForm.scholarships_title,
          scholarships_subtitle: landingPageForm.scholarships_subtitle,
          logo_url: landingPageForm.logo_url,
          banner_url: landingPageForm.banner_url
        }
      };
      await axios.put(`${API}/committees/${selectedCommittee.id}`, payload);
      toast.success('Landing page updated');
      fetchCommittees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save landing page');
    } finally {
      setSavingLandingPage(false);
    }
  };

  const archiveScholarship = async (scholarship) => {
    if (!scholarship?.id) return;
    const confirmed = window.confirm(`Archive ${scholarship.name}?`);
    if (!confirmed) return;

    try {
      await axios.post(`${API}/scholarships/${scholarship.id}/archive`);
      toast.success('Scholarship archived');
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive scholarship');
    }
  };

  const unarchiveScholarship = async (scholarship) => {
    if (!scholarship?.id) return;
    const confirmed = window.confirm(`Restore ${scholarship.name}?`);
    if (!confirmed) return;

    try {
      await axios.post(`${API}/scholarships/${scholarship.id}/unarchive`);
      toast.success('Scholarship restored');
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore scholarship');
    }
  };

  const deleteScholarship = async (scholarship) => {
    if (!scholarship?.id) return;
    const confirmed = window.confirm(`Permanently delete ${scholarship.name}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/scholarships/${scholarship.id}?confirm=true`);
      toast.success('Scholarship deleted');
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete scholarship');
    }
  };

  // Add scoring criterion
  const addScoringCriterion = () => {
    setScholarshipForm({
      ...scholarshipForm,
      scoring_criteria: [
        ...scholarshipForm.scoring_criteria,
        { name: '', weight: 0.1, max_score: 10, description: '' }
      ]
    });
  };

  // Remove scoring criterion
  const removeScoringCriterion = (index) => {
    const newCriteria = scholarshipForm.scoring_criteria.filter((_, i) => i !== index);
    setScholarshipForm({ ...scholarshipForm, scoring_criteria: newCriteria });
  };

  // Update scoring criterion
  const updateScoringCriterion = (index, field, value) => {
    const newCriteria = [...scholarshipForm.scoring_criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setScholarshipForm({ ...scholarshipForm, scoring_criteria: newCriteria });
  };

  // Open scoring dialog for an application
  const openScoringDialog = (application) => {
    const scholarship = scholarships.find(s => s.id === application.scholarship_id);
    setScoringDialog({ open: true, application, scholarship });
  };

  useEffect(() => {
    fetchCommittees();
  }, []);

  useEffect(() => {
    if (selectedCommittee) {
      fetchCommitteeData();
    }
  }, [selectedCommittee]);

  useEffect(() => {
    if (!selectedCommittee) return;
    const landing = selectedCommittee.landing_page || {};
    setLandingPageForm({
      slug: selectedCommittee.slug || '',
      hero_title: landing.hero_title || '',
      hero_subtitle: landing.hero_subtitle || '',
      about_title: landing.about_title || '',
      about_description: landing.about_description || selectedCommittee.description || '',
      contact_email: landing.contact_email || '',
      contact_phone: landing.contact_phone || '',
      contact_address: landing.contact_address || '',
      scholarships_title: landing.scholarships_title || '',
      scholarships_subtitle: landing.scholarships_subtitle || '',
      logo_url: landing.logo_url || '',
      banner_url: landing.banner_url || ''
    });
  }, [selectedCommittee]);
  
  // Fetch scholarship types on mount
  useEffect(() => {
    const fetchScholarshipTypes = async () => {
      try {
        const response = await axios.get(`${API}/settings/scholarship-types`);
        setScholarshipTypes(response.data);
      } catch (error) {
        // Keep defaults on error
      }
    };
    fetchScholarshipTypes();
  }, []);

  const fetchCommittees = async () => {
    try {
      const response = await axios.get(`${API}/committees`);
      setCommittees(response.data);
      
      // Check for committee query param (used by admin to navigate to specific committee)
      const committeeParam = searchParams.get('committee');
      const tabParam = searchParams.get('tab');
      
      if (committeeParam && response.data.length > 0) {
        const targetCommittee = response.data.find(c => c.id === committeeParam);
        if (targetCommittee) {
          setSelectedCommittee(targetCommittee);
        } else if (response.data.length > 0) {
          setSelectedCommittee(response.data[0]);
        }
      } else if (selectedCommittee?.id) {
        const matched = response.data.find(c => c.id === selectedCommittee.id);
        if (matched) {
          setSelectedCommittee(matched);
        } else if (response.data.length > 0) {
          setSelectedCommittee(response.data[0]);
        }
      } else if (response.data.length > 0) {
        setSelectedCommittee(response.data[0]);
      }
      
      // Set active tab from URL param if provided
      if (tabParam && ['scholarships', 'requests', 'members', 'applicants', 'templates', 'landing-page', 'merit-report', 'library'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    } catch (error) {
      toast.error('Failed to load committees');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommitteeData = async () => {
    try {
      const [scholarshipsRes, requestsRes, membersRes, applicationsRes, allScholarshipsRes] = await Promise.allSettled([
        axios.get(`${API}/committees/${selectedCommittee.id}/scholarships`),
        axios.get(`${API}/committees/${selectedCommittee.id}/access-requests`),
        axios.get(`${API}/committees/${selectedCommittee.id}/members`),
        axios.get(`${API}/committees/${selectedCommittee.id}/applications?include_archived=${showArchived}`),
        axios.get(`${API}/scholarships`)
      ]);

      if (scholarshipsRes.status === 'fulfilled') setScholarships(scholarshipsRes.value.data);
      if (requestsRes.status === 'fulfilled') setAccessRequests(requestsRes.value.data);
      if (membersRes.status === 'fulfilled') setMembers(membersRes.value.data);
      if (applicationsRes.status === 'fulfilled') setApplications(applicationsRes.value.data);
      if (allScholarshipsRes.status === 'fulfilled') setAllScholarships(allScholarshipsRes.value.data);
    } catch (error) {
      // Silently handle - user may not have access to all endpoints
      console.error('Error fetching committee data:', error);
    }
  };

  // Refetch applications when showArchived changes
  useEffect(() => {
    if (selectedCommittee) {
      axios.get(`${API}/committees/${selectedCommittee.id}/applications?include_archived=${showArchived}`)
        .then(res => setApplications(res.data))
        .catch(err => console.error('Error fetching applications:', err));
    }
  }, [showArchived, selectedCommittee]);

  const handleLogout = () => {
    logout();
  };

  const getUserRole = () => {
    if (!selectedCommittee || !user) return null;
    // Admin users are shown as Admin regardless of committee membership
    if (user?.role === 'admin') return 'admin';
    const member = selectedCommittee.members?.find(m => m.user_id === user.id);
    return member?.role;
  };

  const isChairOrCoChair = () => {
    const role = getUserRole();
    return role === 'chair' || role === 'co_chair' || user?.role === 'admin';
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const getRoleDisplay = (role) => {
    const roles = {
      'admin': 'Administrator',
      'chair': 'Chair',
      'co_chair': 'Co-Chair',
      'member': 'Member'
    };
    return roles[role] || role;
  };

  const getScoreSummaryLabel = (application) => {
    const summary = application?.score_summary;
    if (!summary?.has_scores || summary.average_score === null || summary.average_score === undefined) {
      return null;
    }

    return `${summary.average_score} / ${summary.max_possible_score || 0}`;
  };

  const inviteMember = async () => {
    try {
      const response = await axios.post(
        `${API}/committees/${selectedCommittee.id}/invite`,
        inviteForm
      );
      toast.success(`Invitation sent to ${inviteForm.email}`);
      if (response.data.email_sent) {
        toast.info('Email notification sent');
      }
      setInviteDialog(false);
      setInviteForm({ email: '', first_name: '', last_name: '', role: 'member' });
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const reviewAccessRequest = async (requestId, token, approved) => {
    try {
      await axios.post(`${API}/access-request/${token}/review`, null, {
        params: { approved }
      });
      toast.success(`Access ${approved ? 'approved' : 'denied'}`);
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review request');
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await axios.delete(`${API}/committees/${selectedCommittee.id}/members/${userId}`);
      toast.success('Member removed');
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  // Open invite dialog and fetch member limits
  const openInviteDialog = async () => {
    try {
      const response = await axios.get(`${API}/committees/${selectedCommittee.id}/member-limits`);
      setMemberLimits(response.data);
      
      // Auto-select the first available role
      const available = response.data.available;
      let defaultRole = 'member';
      if (available.member <= 0 && available.co_chair > 0) {
        defaultRole = 'co_chair';
      }
      setInviteForm({ email: '', first_name: '', last_name: '', role: defaultRole });
      setInviteDialog(true);
    } catch (error) {
      console.error('Failed to fetch member limits:', error);
      // Open dialog anyway with default values
      setMemberLimits(null);
      setInviteForm({ email: '', first_name: '', last_name: '', role: 'member' });
      setInviteDialog(true);
    }
  };

  // Archive an application
  const archiveApplication = async () => {
    if (!archiveDialog.application) return;
    
    try {
      await axios.post(`${API}/applications/${archiveDialog.application.id}/archive`, archiveForm);
      toast.success('Application archived successfully');
      setArchiveDialog({ open: false, application: null });
      setArchiveForm({ reason: '', archive_user: false });
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive application');
    }
  };

  // Unarchive an application
  const unarchiveApplication = async (applicationId) => {
    try {
      await axios.post(`${API}/applications/${applicationId}/unarchive`);
      toast.success('Application unarchived');
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unarchive application');
    }
  };

  // Fetch merit report
  const fetchMeritReport = async (scale = selectedGpaScale) => {
    if (!selectedCommittee) return;
    
    setMeritLoading(true);
    try {
      const response = await axios.get(`${API}/committees/${selectedCommittee.id}/merit-report?normalize_scale=${scale}`);
      setMeritReport(response.data);
    } catch (error) {
      console.error('Failed to fetch merit report:', error);
      toast.error('Failed to load merit report');
    } finally {
      setMeritLoading(false);
    }
  };

  // Handle GPA scale change
  const handleScaleChange = (scale) => {
    setSelectedGpaScale(scale);
    fetchMeritReport(scale);
  };

  // Award scholarship to applicant
  const awardScholarship = async () => {
    if (!awardDialog.application) return;
    
    try {
      await axios.post(`${API}/applications/${awardDialog.application.id}/award`, {
        application_id: awardDialog.application.id,
        scholarship_id: awardDialog.application.scholarship_id,
        scholarship_name: awardDialog.application.scholarship_name,
        committee_id: selectedCommittee.id,
        committee_name: selectedCommittee.name,
        amount: 0, // Will be populated from scholarship
        award_year: new Date().getFullYear().toString(),
        awarded_by: user?.id,
        notes: awardNotes
      });
      toast.success('Scholarship awarded successfully!');
      setAwardDialog({ open: false, application: null });
      setAwardNotes('');
      fetchCommitteeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to award scholarship');
    }
  };

  // View applicant profile
  const viewApplicantProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/applicants/${userId}/profile`);
      setProfileDialog({ open: true, applicant: response.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load applicant profile');
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const badges = {
      'draft': 'bg-slate-100 text-slate-600',
      'submitted': 'bg-blue-100 text-blue-700',
      'under_review': 'bg-yellow-100 text-yellow-700',
      'awarded': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700',
      'archived': 'bg-slate-200 text-slate-500'
    };
    return badges[status] || 'bg-slate-100 text-slate-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (committees.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">No Committees Found</h1>
          <p className="text-slate-600 mb-6">You are not a member of any committees yet.</p>
          <Button onClick={() => navigate('/')} className="bg-greek-blue hover:bg-blue-700">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  const pendingRequests = accessRequests.filter(r => r.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
                alt="AHEPA Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
                alt="Daughters of Penelope Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold" style={{ color: '#0D5EAF' }}>AHEPA Family</h1>
              <p className="text-xs text-slate-500">Committee Dashboard</p>
            </div>
          </div>
          
          {/* Committee Selector - More Prominent */}
          {committees.length > 1 && (
            <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-lg border border-slate-200">
              <Building2 className="w-5 h-5 text-greek-blue" />
              <Select
                value={selectedCommittee?.id}
                onValueChange={(value) => setSelectedCommittee(committees.find(c => c.id === value))}
              >
                <SelectTrigger className="w-64 border-0 bg-transparent focus:ring-0">
                  <SelectValue placeholder="Select committee" />
                </SelectTrigger>
                <SelectContent>
                  {committees.map((committee) => (
                    <SelectItem key={committee.id} value={committee.id}>
                      {committee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">
                {committees.length} committees
              </span>
            </div>
          )}
          
          <div className="flex items-center gap-4">
            {/* Back to My Committees - for non-admin committee members */}
            {user?.role !== 'admin' && committees.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/my-committees')}
                className="rounded-sm border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="back-to-committees-btn"
              >
                ← My Committees
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="rounded-sm border-greek-blue text-greek-blue hover:bg-greek-blue/10"
                data-testid="back-to-admin-btn"
              >
                ← Back to Admin
              </Button>
            )}
            <div className="text-right">
              <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500">
                {user?.role === 'admin' ? (
                  <span className="text-greek-blue font-medium">Admin Support Mode</span>
                ) : (
                  getRoleDisplay(getUserRole())
                )}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-sm border-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Committee Header */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-greek-blue/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-7 h-7 text-greek-blue" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{selectedCommittee?.name}</h2>
                <p className="text-slate-600">{selectedCommittee?.description || 'Scholarship Committee'}</p>
              </div>
            </div>
            
            {isChairOrCoChair() && (
              <Button
                onClick={openInviteDialog}
                className="bg-greek-blue hover:bg-blue-700"
                data-testid="invite-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Double-click to navigate to corresponding tab */}
        <div className={`grid gap-4 mb-6 ${isChairOrCoChair() ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
          <div 
            className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:border-greek-blue hover:shadow-md transition-all"
            onDoubleClick={() => setActiveTab('scholarships')}
            title="Double-click to view scholarships"
            data-testid="stats-card-scholarships"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-greek-blue/10 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-greek-blue" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{scholarships.length}</p>
                <p className="text-sm text-slate-500">Scholarships</p>
              </div>
            </div>
          </div>
          
          {isChairOrCoChair() && (
            <div 
              className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:border-greek-blue hover:shadow-md transition-all"
              onDoubleClick={() => setActiveTab('members')}
              title="Double-click to view members"
              data-testid="stats-card-members"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-greek-blue/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-greek-blue" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{members.length}</p>
                  <p className="text-sm text-slate-500">Members</p>
                </div>
              </div>
            </div>
          )}
          
          {isChairOrCoChair() && (
            <div 
              className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:border-yellow-500 hover:shadow-md transition-all"
              onDoubleClick={() => setActiveTab('access-requests')}
              title="Double-click to view pending requests"
              data-testid="stats-card-pending-requests"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pendingRequests.length}</p>
                  <p className="text-sm text-slate-500">Pending Requests</p>
                </div>
              </div>
            </div>
          )}
          
          <div 
            className="bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
            onDoubleClick={() => setActiveTab('applicants')}
            title="Double-click to view applications"
            data-testid="stats-card-applications"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {applications.filter(a => a.status === 'submitted').length}
                </p>
                <p className="text-sm text-slate-500">Applications to Review</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-slate-200 p-1 rounded-sm">
            <TabsTrigger 
              value="scholarships" 
              className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
            >
              <Award className="w-4 h-4 mr-2" />
              Scholarships
            </TabsTrigger>
            {isChairOrCoChair() && (
              <>
                <TabsTrigger 
                  value="requests" 
                  className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Access Requests
                  {pendingRequests.length > 0 && (
                    <span className="ml-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {pendingRequests.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="members" 
                  className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Members
                </TabsTrigger>
              </>
            )}
            {/* Applicants tab visible to all committee members for scoring */}
            <TabsTrigger 
              value="applicants" 
              className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
              data-testid="tab-applicants"
            >
              <FileText className="w-4 h-4 mr-2" />
              Applicants
              {applications.filter(a => a.status === 'submitted').length > 0 && (
                <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {applications.filter(a => a.status === 'submitted').length}
                </span>
              )}
            </TabsTrigger>
            {isChairOrCoChair() && (
              <TabsTrigger 
                value="templates" 
                className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                data-testid="tab-templates"
              >
                <Palette className="w-4 h-4 mr-2" />
                Templates
              </TabsTrigger>
            )}
            {isChairOrCoChair() && (
              <TabsTrigger
                value="landing-page"
                className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                data-testid="tab-landing-page"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Landing Page
              </TabsTrigger>
            )}
            {isChairOrCoChair() && (
              <TabsTrigger 
                value="merit-report" 
                className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                data-testid="tab-merit-report"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Merit Report
              </TabsTrigger>
            )}
                      {isChairOrCoChair() && (
              <TabsTrigger 
                value="library" 
                className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                data-testid="tab-library"
              >
                <Search className="w-4 h-4 mr-2" />
                Form Library
              </TabsTrigger>
            )}
          </TabsList>

          {/* Scholarships Tab */}
          <TabsContent value="scholarships">
            <CommitteeScholarshipsTab
              scholarships={scholarships}
              isChairOrCoChair={isChairOrCoChair}
              openScholarshipDialog={openScholarshipDialog}
              archiveScholarship={archiveScholarship}
              unarchiveScholarship={unarchiveScholarship}
              deleteScholarship={deleteScholarship}
            />
          </TabsContent>

          {/* Access Requests Tab */}
          <TabsContent value="requests">
            <CommitteeAccessRequestsTab
              accessRequests={accessRequests}
              reviewAccessRequest={reviewAccessRequest}
            />
          </TabsContent>

          <TabsContent value="members">
            <CommitteeMembersTab
              members={members}
              user={user}
              isChairOrCoChair={isChairOrCoChair}
              getRoleDisplay={getRoleDisplay}
              removeMember={removeMember}
            />
          </TabsContent>

          <TabsContent value="applicants">
            <CommitteeApplicantsTab
              applications={applications}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              getStatusBadge={getStatusBadge}
              getScoreSummaryLabel={getScoreSummaryLabel}
              viewApplicantProfile={viewApplicantProfile}
              downloadApplicationPdf={downloadApplicationPdf}
              downloadingPdf={downloadingPdf}
              openScoringDialog={openScoringDialog}
              setAwardDialog={setAwardDialog}
              setArchiveDialog={setArchiveDialog}
              unarchiveApplication={unarchiveApplication}
              isChairOrCoChair={isChairOrCoChair}
            />
          </TabsContent>

          <TabsContent value="library">
            <CommitteeLibraryTab
              allScholarships={allScholarships}
              currentCommitteeId={selectedCommittee?.id}
              cloneFromLibrary={cloneFromLibrary}
            />
          </TabsContent>

          <TabsContent value="templates">
            <TemplateManager committeeId={selectedCommittee?.id} isAdmin={user?.role === 'admin'} />
          </TabsContent>

          <TabsContent value="landing-page">
            <CommitteeLandingPageTab
              committee={selectedCommittee}
              landingPageForm={landingPageForm}
              setLandingPageForm={setLandingPageForm}
              saveLandingPage={saveLandingPage}
              savingLandingPage={savingLandingPage}
            />
          </TabsContent>

          {/* Merit Report Tab */}
          <TabsContent value="merit-report">
            <CommitteeMeritReportTab
              meritReport={meritReport}
              meritLoading={meritLoading}
              selectedGpaScale={selectedGpaScale}
              handleScaleChange={handleScaleChange}
              fetchMeritReport={fetchMeritReport}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Committee Member</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                  placeholder="John"
                  data-testid="invite-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                  placeholder="Doe"
                  data-testid="invite-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="invite-email"
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {/* Chair option - only shown if user can invite chairs (committee has at least 1 chair) */}
                  {memberLimits?.can_invite_chair && (
                    <SelectItem 
                      value="chair" 
                      disabled={memberLimits && memberLimits.available.chair <= 0}
                    >
                      Chair {memberLimits && `(${memberLimits.available.chair} of ${memberLimits.limits.chair} available)`}
                    </SelectItem>
                  )}
                  <SelectItem 
                    value="co_chair" 
                    disabled={memberLimits && memberLimits.available.co_chair <= 0}
                  >
                    Co-Chair {memberLimits && `(${memberLimits.available.co_chair} of ${memberLimits.limits.co_chair} available)`}
                  </SelectItem>
                  <SelectItem 
                    value="member"
                    disabled={memberLimits && memberLimits.available.member <= 0}
                  >
                    Committee Member {memberLimits && `(${memberLimits.available.member} of ${memberLimits.limits.member} available)`}
                  </SelectItem>
                </SelectContent>
              </Select>
              {memberLimits && (
                <p className="text-xs text-slate-500 mt-1">
                  Current: {memberLimits.current.chair} Chair(s), {memberLimits.current.co_chair} Co-Chair(s), {memberLimits.current.member} Member(s)
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={inviteMember}
              className="bg-greek-blue hover:bg-blue-700"
              disabled={!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name || 
                (memberLimits && memberLimits.available[inviteForm.role] <= 0)}
              data-testid="send-invite-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Application Dialog */}
      <Dialog open={archiveDialog.open} onOpenChange={(open) => !open && setArchiveDialog({ open: false, application: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Application</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Archive the application from <strong>{archiveDialog.application?.applicant_name}</strong> for {archiveDialog.application?.scholarship_name}?
            </p>
            
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={archiveForm.reason}
                onChange={(e) => setArchiveForm({ ...archiveForm, reason: e.target.value })}
                placeholder="e.g., Application incomplete, Duplicate, etc."
              />
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={archiveForm.archive_user}
                    onChange={(e) => setArchiveForm({ ...archiveForm, archive_user: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <span className="text-amber-800">Also archive the user account (prevents login)</span>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialog({ open: false, application: null })}>
              Cancel
            </Button>
            <Button
              onClick={archiveApplication}
              className="bg-slate-600 hover:bg-slate-700"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Scholarship Dialog */}
      <Dialog open={awardDialog.open} onOpenChange={(open) => !open && setAwardDialog({ open: false, application: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-green-600" />
              Award Scholarship
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800">
                You are about to award the <strong>{awardDialog.application?.scholarship_name}</strong> scholarship to <strong>{awardDialog.application?.applicant_name}</strong>.
              </p>
              <p className="text-xs text-green-700 mt-2">
                This action will mark the applicant as a scholarship recipient and will be visible on their profile.
              </p>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={awardNotes}
                onChange={(e) => setAwardNotes(e.target.value)}
                placeholder="e.g., Outstanding academic achievement"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAwardDialog({ open: false, application: null })}>
              Cancel
            </Button>
            <Button
              onClick={awardScholarship}
              className="bg-green-600 hover:bg-green-700"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Confirm Award
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applicant Profile Dialog */}
      <ApplicantProfileModal
        profileDialog={profileDialog}
        setProfileDialog={setProfileDialog}
        getStatusBadge={getStatusBadge}
      />

      {/* Scholarship Create/Edit Dialog */}
      <Dialog open={scholarshipDialog.open} onOpenChange={(open) => !open && setScholarshipDialog({ open: false, editing: null })}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {scholarshipDialog.editing ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {scholarshipDialog.editing ? 'Edit Scholarship' : 'Create New Scholarship'}
            </DialogTitle>
            <DialogDescription>
              Update scholarship details, application form, and scoring settings before saving.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={editTab} onValueChange={setEditTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="scoring">Scoring Criteria</TabsTrigger>
              <TabsTrigger value="form">Application Form</TabsTrigger>
                        {isChairOrCoChair() && (
              <TabsTrigger 
                value="library" 
                className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
                data-testid="tab-library"
              >
                <Search className="w-4 h-4 mr-2" />
                Form Library
              </TabsTrigger>
            )}
          </TabsList>
            
            {/* Details Tab */}
            <TabsContent value="details" className="flex-1 overflow-y-auto p-1">
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Scholarship Name *</Label>
                  <Input
                    id="name"
                    value={scholarshipForm.name}
                    onChange={(e) => setScholarshipForm({ ...scholarshipForm, name: e.target.value })}
                    placeholder="Enter scholarship name"
                    className="rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={scholarshipForm.description}
                    onChange={(e) => setScholarshipForm({ ...scholarshipForm, description: e.target.value })}
                    placeholder="Describe the scholarship"
                    className="rounded-sm min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Award Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={scholarshipForm.amount}
                      onChange={(e) => setScholarshipForm({ ...scholarshipForm, amount: parseFloat(e.target.value) || 0 })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Scholarship Type</Label>
                    <Select
                      value={scholarshipForm.scholarship_type}
                      onValueChange={(value) => setScholarshipForm({ ...scholarshipForm, scholarship_type: value })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {scholarshipTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={scholarshipForm.start_date}
                      onChange={(e) => setScholarshipForm({ ...scholarshipForm, start_date: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deadline">Application Deadline *</Label>
                    <Input
                      id="deadline"
                      type="datetime-local"
                      value={scholarshipForm.deadline}
                      onChange={(e) => setScholarshipForm({ ...scholarshipForm, deadline: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eligibility">Eligibility Requirements</Label>
                  <Textarea
                    id="eligibility"
                    value={scholarshipForm.eligibility}
                    onChange={(e) => setScholarshipForm({ ...scholarshipForm, eligibility: e.target.value })}
                    placeholder="List eligibility requirements"
                    className="rounded-sm min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={scholarshipForm.is_active ? "active" : "inactive"}
                      onValueChange={(value) => setScholarshipForm({ ...scholarshipForm, is_active: value === "active" })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Allow Previous Recipients</Label>
                    <Select
                      value={scholarshipForm.allow_previous_recipients ? "yes" : "no"}
                      onValueChange={(value) => setScholarshipForm({ ...scholarshipForm, allow_previous_recipients: value === "yes" })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Scoring Template Tab */}
            <TabsContent value="scoring" className="flex-1 overflow-y-auto p-1">
              <ScoringTemplateEditor
                formConfig={scholarshipForm.form_config}
                scoringTemplate={scholarshipForm.scoring_template}
                onChange={(template) => setScholarshipForm((currentForm) => ({ ...currentForm, scoring_template: template }))}
              />
            </TabsContent>
            
            {/* Form Builder Tab */}
            <TabsContent value="form" className="flex-1 overflow-y-auto p-1">
              <FormBuilder 
                formConfig={scholarshipForm.form_config}
                onChange={(newConfig) => setScholarshipForm({ ...scholarshipForm, form_config: newConfig })}
              />
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="border-t pt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {scholarshipDialog.editing && (
                <Button
                  variant="outline"
                  onClick={() => saveScholarship(false)}
                  disabled={savingScholarship}
                  className="rounded-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Scholarship
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setScholarshipDialog({ open: false, editing: null })} className="rounded-sm">
                Cancel
              </Button>
              <Button 
                onClick={() => saveScholarship(true)}
                disabled={savingScholarship || !scholarshipForm.name || !scholarshipForm.deadline}
                className="bg-greek-blue hover:bg-greek-blue-dark rounded-sm"
              >
                {savingScholarship ? 'Saving...' : (scholarshipDialog.editing ? 'Save & Close' : 'Create Scholarship')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Scoring Dialog */}
      <Dialog open={scoringDialog.open} onOpenChange={(open) => !open && setScoringDialog({ open: false, application: null, scholarship: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-greek-blue" />
              Score Application
            </DialogTitle>
            <DialogDescription data-testid="scoring-dialog-description">
              Review applicant responses, add notes, and save or submit committee scoring.
            </DialogDescription>
          </DialogHeader>
          
          {scoringDialog.application && scoringDialog.scholarship && (
            <ApplicationScoring
              application={scoringDialog.application}
              scholarship={scoringDialog.scholarship}
              onScoreSubmit={({ isComplete }) => {
                fetchCommitteeData();
                if (isComplete) {
                  setScoringDialog({ open: false, application: null, scholarship: null });
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommitteeDashboard;
